import { Button } from '@sunwebgroup/uds-ui/button'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { TitleListCell } from '@sunwebgroup/uds-ui/title-list-cell'
import cn from 'classnames'
import * as React from 'react'
import { useIntl } from '@/intl'
import { CheckoutSection } from '@/scopes/checkout/components/CheckoutSection'
import { createGroupsEvent } from '@/scopes/checkout/domain/analytics'
import { useBookingDraftQuery, useCheckoutSid } from '@/scopes/checkout/domain/hooks'
import {
  getNewRoomBeingCreated,
  getSelectedRoom,
  isNewRoomBeingCreated,
  isRandomAllocationSelected,
  isRoomSelected,
} from '@/scopes/checkout/groups/components/services/RoomSelection/selectors'
import { RoomSelectionServiceSheet } from '@/scopes/checkout/groups/components/services/RoomSelection/Sheet/Sheet'
import { useRoomSelectionServiceStore } from '@/scopes/checkout/groups/components/services/RoomSelection/store'
import { useAvailableRoomsQuery } from '@/scopes/checkout/groups/domain/hooks'
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import styles from './RoomSelectionService.module.css'

export function RoomSelectionService() {
  const intl = useIntl()
  const sid = useCheckoutSid()
  const { isLoading } = useBookingDraftQuery()

  if (!sid) {
    return null
  }

  if (isLoading)
    return

  return (
    <CheckoutSection
      title={intl.formatMessage({ id: 'groups_checkout_steps_room_selection_title' })}
      description={intl.formatMessage({ id: 'groups_checkout_steps_room_selection_description' })}
      glyph="Bed"
    >
      <RoomSelectionSheetTrigger />
      <RoomSelectionServiceSheet />
    </CheckoutSection>
  )
}

function RoomSelectionSheetTrigger() {
  const { isDesktopSm } = useBreakpoints()
  const intl = useIntl()
  const { startSelectingRoomService } = useRoomSelectionServiceStore()
  const { data: bookingDraftData } = useBookingDraftQuery()
  const { sendEvent } = useAnalytics()

  const handleClick = React.useCallback(() => {
    startSelectingRoomService()
    sendEvent(
      createGroupsEvent({
        description: 'RoomSelection',
        element: 'button',
        type: 'toggle',
        result: 'open sheet',
      }),
    )
  }, [sendEvent, startSelectingRoomService])

  const newRoomBeingCreated = isNewRoomBeingCreated(bookingDraftData)
  const roomIsSelected = isRoomSelected(bookingDraftData)
  const randomAllocationSelected = isRandomAllocationSelected(bookingDraftData)
  const hasSelection = (roomIsSelected || randomAllocationSelected || newRoomBeingCreated)

  return (
    <ListItem
      variant="outlined"
      className={cn(hasSelection ? styles.listItemContainerSpaceBetween : styles.listItemContainer)}
      onClick={handleClick}
    >
      {!hasSelection && (
        <Button size="S" variant="secondary">
          {intl.formatMessage({ id: 'groups_checkout_steps_room_selection_select_button' })}
        </Button>
      )}

      {hasSelection && (
        <>
          { (roomIsSelected && !newRoomBeingCreated) && <SelectedRoom /> }
          { newRoomBeingCreated && <NewRoomBeingCreated /> }
          { randomAllocationSelected && <RandomAllocationSelected /> }
          {isDesktopSm
            ? (
                <Button variant="secondary" size="S" className={styles.editButton}>
                  {intl.formatMessage({ id: 'groups_checkout_steps_room_selection_edit_button' })}
                </Button>
              )
            : (
                <Button variant="transparentNeutral" className={styles.editButton}>
                  <Button.Icon glyph="ChevronRight" size={24} />
                </Button>
              )}
        </>
      )}
    </ListItem>
  )
}

function NewRoomBeingCreated() {
  const intl = useIntl()
  const { data: bookingDraftData } = useBookingDraftQuery()
  const { data } = useAvailableRoomsQuery()
  const { roomTypes } = data || {}
  const newRoom = getNewRoomBeingCreated(roomTypes, bookingDraftData)

  return (
    <TitleListCell
      title={newRoom?.name || ''}
      subtitle={intl.formatMessage(
        { id: 'groups_checkout_steps_room_selection_selected_room_subtitle' },
        {
          amount: newRoom?.availableBedsAmount,
          total: newRoom?.totalBedsAmount,
          roomType: newRoom?.description,
        },
      )}
    />
  )
}

function RandomAllocationSelected() {
  const intl = useIntl()
  return <TitleListCell title={intl.formatMessage({ id: 'groups_checkout_steps_room_selection_random_room_allocation' })} />
}

function SelectedRoom() {
  const intl = useIntl()
  const { data: bookingDraftData } = useBookingDraftQuery()
  const { data } = useAvailableRoomsQuery()
  const { rooms } = data || {}

  const selectedRoom = getSelectedRoom(rooms, bookingDraftData)

  if (!selectedRoom) {
    return null
  }
  return (
    <TitleListCell
      title={selectedRoom.name}
      subtitle={intl.formatMessage(
        { id: 'groups_checkout_steps_room_selection_selected_room_subtitle' },
        {
          amount: selectedRoom.availableBedsAmount,
          total: selectedRoom.totalBedsAmount,
          roomType: selectedRoom.description,
        },
      )}
    />
  )
}
